import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-center text-xl" }
const _hoisted_2 = { class: "font-bold" }

import debounce from 'lodash/debounce';
import { markRaw, onMounted, ref, watch } from 'vue';
import { IPhieuXuatKhoResponseBase } from '@/core/interfaces/ApiResponses';
import PhieuXuatKhoService from '@/core/services/Receipt.service';
import { swalErrNotification, swalNotification } from '@/core/helpers/utils';
import { usePagination } from '@/core/hooks/use-pagination';
import { useLoading } from '@/core/hooks/use-loading';
import { usePageHeader } from '@/core/hooks/usePageHeader';
import usePermission from '@/core/hooks/usePermission';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ButtonsType } from '@/core/enums/buttons.enums';
import { openPhieuXuatKhoPdf } from '@/components/shared/pdf-viewer/open-phieu-xuat-kho-pdf';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import Pagination from '@/components/shared/pagination/Pagination.vue';
import PhieuXuatKhoTable from '@/components/phieu-xuat-kho/PhieuXuatKhoTable.vue';
import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog.vue';
import ConfirmHoanThanh from '@/components/shared/confirm-hoan-thanh-modal/ConfirmHoanThanh.vue';
import IconCircleCheckClassicRegular from '@/icons/IconCircleCheckClassicRegular.vue';
import IconEye from '@/icons/IconEye.vue';
import IconMemoCircleCheckClassicSolid from '@/icons/IconMemoCircleCheckClassicSolid.vue';
import IconTrash from '@/icons/IconTrash.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'List',
  setup(__props) {

document.title = 'Phê duyệt phiếu xuất kho - Đường sắt Hà Nội';

const { currentPage, totalItems, perPage } = usePagination();
const { startLoading, endLoading } = useLoading();
const items = ref<IPhieuXuatKhoResponseBase[]>([]);
const { canDuyetPxk } = usePermission();
const router = useRouter();

const isDeleteDialogDisplay = ref(false);
const isApproveDialogDisplay = ref(false);
const selectedPhieuXuatKho = ref<IPhieuXuatKhoResponseBase | null>(null);

const { searchQuery } = usePageHeader({
  title: 'Phê duyệt phiếu xuất kho',
  breadcrumbs: [
    { text: 'Phê duyệt' },
    { text: 'Phiếu xuất kho' },
  ],
  showSearch: true,
  searchPlaceholder: 'Số phiếu hoặc người nhận',
});

const actionButtons = [
  {
    buttonType: ButtonsType.INFO,
    icon: markRaw(IconEye),
    label: 'Xem chi tiết và in phiếu',
    tooltipMessage: 'Xem chi tiết và in phiếu',
    condition: () => true,
    onClick: (row: IPhieuXuatKhoResponseBase) => onInfoHandler(row),
  },
  {
    buttonType: ButtonsType.TRASH,
    icon: markRaw(IconTrash),
    label: 'Xóa phiếu xuất kho',
    tooltipMessage: 'Xóa phiếu xuất kho',
    condition: (row: IPhieuXuatKhoResponseBase) =>
      !row.is_approved && !row.hoan_thanh,
    onClick: (row: IPhieuXuatKhoResponseBase) => onDeleteHandler(row),
  },
  {
    buttonType: ButtonsType.CONFIRM,
    icon: markRaw(IconMemoCircleCheckClassicSolid),
    label: 'Xác nhận phiếu xuất kho',
    tooltipMessage: 'Xác nhận phiếu xuất kho',
    condition: (row: IPhieuXuatKhoResponseBase) => !row.is_approved,
    onClick: (row: IPhieuXuatKhoResponseBase) => onApproveHandler(row),
  },
  {
    buttonType: ButtonsType.APPROVE,
    icon: markRaw(IconCircleCheckClassicRegular),
    label: 'Duyệt phiếu xuất kho',
    tooltipMessage: 'Duyệt phiếu xuất kho',
    condition: () => true,
    onClick: (row: IPhieuXuatKhoResponseBase) => handleAdminSignButtonClicked(row),
  },
];

const fetchPhieuXuatKhoItems = async () => {
  try {
    startLoading();

    const {
      data: {
        data: { data, total },
      },
    } = await PhieuXuatKhoService.getListForApproval({
      per_page: perPage.value,
      current_page: currentPage.value,
      search: searchQuery.value,
    });

    totalItems.value = total;
    items.value = data;
    endLoading();
  } catch (e) {
    await swalNotification(
      'Có lỗi xảy ra khi lấy thông tin danh sách phiếu xuất kho',
      'error'
    );
    endLoading();
  }
};

watch(
  () => currentPage.value,
  async () => {
    await fetchPhieuXuatKhoItems();
  }
);

watch(
  () => perPage.value,
  () => {
    if (currentPage.value === 1) {
      fetchPhieuXuatKhoItems();
      return;
    }
    currentPage.value = 1;
  }
);

watch(searchQuery, debounce(() => {
  fetchPhieuXuatKhoItems();
}, 500));

const onInfoHandler = (phieuXuatKho: IPhieuXuatKhoResponseBase) => {
  openPhieuXuatKhoPdf(router, phieuXuatKho.id);
};

const onDeleteHandler = (phieuXuatKho: IPhieuXuatKhoResponseBase) => {
  selectedPhieuXuatKho.value = phieuXuatKho;
  isDeleteDialogDisplay.value = true;
};

const onApproveHandler = (phieuXuatKho: IPhieuXuatKhoResponseBase) => {
  selectedPhieuXuatKho.value = phieuXuatKho;
  isApproveDialogDisplay.value = true;
};

const { commit } = useStore();

const handleAdminSignButtonClicked = (phieuXuatKho: IPhieuXuatKhoResponseBase) => {
  commit('ConfirmationDialogModule/setDialog', {
    title: `Phiếu xuất kho số ${phieuXuatKho.so_phieu}`,
    message: 'Xác nhận duyệt phiếu xuất kho này?',
    onConfirm: async () => {
      commit('ConfirmationDialogModule/closeDialog');
      await startLoading();

      try {
        await PhieuXuatKhoService.adminSign(phieuXuatKho.id);
        await endLoading();
        await swalNotification('Duyệt phiếu xuất kho thành công');
        await fetchPhieuXuatKhoItems();
      } catch (e) {
        await endLoading();
        await swalErrNotification(e, 'Có lỗi xảy ra');
      }
    }
  });
};

const confirmDelete = async () => {
  if (!selectedPhieuXuatKho.value) return;

  try {
    startLoading();

    await PhieuXuatKhoService.delete(selectedPhieuXuatKho.value.id);

    await swalNotification('Xóa phiếu xuất kho thành công', 'success');

    isDeleteDialogDisplay.value = false;
    selectedPhieuXuatKho.value = null;
    await fetchPhieuXuatKhoItems();

    endLoading();
  } catch (e) {
    await swalNotification('Có lỗi xảy ra khi xóa phiếu xuất kho', 'error');
    endLoading();
  }
};

const confirmHoanThanh = async () => {
  selectedPhieuXuatKho.value = null;
  isApproveDialogDisplay.value = false;
  await fetchPhieuXuatKhoItems();
};

const closeConfirmHoanThanh = () => {
  selectedPhieuXuatKho.value = null;
  isApproveDialogDisplay.value = false;
};

onMounted(async () => {
  if (!canDuyetPxk.value) {
    await swalNotification(
      'Bạn không có quyền thực hiện chức năng này',
      'warning'
    );

    router.replace('/trang-chu');

    return;
  }

  await fetchPhieuXuatKhoItems();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ConfirmationDialog, {
      title: "Xác nhận xóa",
      size: "sm",
      "is-display": isDeleteDialogDisplay.value,
      onOnConfirmButtonClick: confirmDelete,
      onOnCloseButtonClick: _cache[0] || (_cache[0] = ($event: any) => (isDeleteDialogDisplay.value = false)),
      onOnCloseIconClick: _cache[1] || (_cache[1] = ($event: any) => (isDeleteDialogDisplay.value = false))
    }, {
      default: _withCtx(() => [
        _createElementVNode("h4", _hoisted_1, [
          _createTextVNode(" Bạn có chắc chắn muốn xóa phiếu xuất kho số "),
          _createElementVNode("span", _hoisted_2, _toDisplayString(selectedPhieuXuatKho.value?.so_phieu), 1),
          _createTextVNode("? ")
        ])
      ]),
      _: 1
    }, 8, ["is-display"]),
    _createVNode(ConfirmHoanThanh, {
      for: 'phieu_xuat',
      "phieu-xuat-data": selectedPhieuXuatKho.value,
      show: !!selectedPhieuXuatKho.value && isApproveDialogDisplay.value,
      onClose: closeConfirmHoanThanh,
      onConfirm: confirmHoanThanh
    }, null, 8, ["phieu-xuat-data", "show"]),
    _createVNode(PageContentBase, null, {
      default: _withCtx(() => [
        _createVNode(PhieuXuatKhoTable, {
          "cur-page": _unref(currentPage),
          items: items.value,
          "per-page": _unref(perPage),
          "show-xac-nhan-button": false,
          "show-xuat-tai-kho-column": true,
          "action-buttons": actionButtons,
          onRefresh: fetchPhieuXuatKhoItems
        }, null, 8, ["cur-page", "items", "per-page"]),
        (items.value.length > 0)
          ? (_openBlock(), _createBlock(Pagination, {
              key: 0,
              page: _unref(currentPage),
              "page-size": _unref(perPage),
              total: _unref(totalItems),
              onChangePage: _cache[2] || (_cache[2] = (value) => (currentPage.value = value)),
              onChangePageSize: _cache[3] || (_cache[3] = (value) => (perPage.value = value))
            }, null, 8, ["page", "page-size", "total"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})